export const SITE_NAME = "CAJobPortal";
export const SITE_URL = "https://cajobportal.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const DEFAULT_DESCRIPTION =
  "India's first recruitment portal exclusive for CA, CS, and CMA professionals. Browse latest jobs, apply online, and hire finance talent.";

export const absoluteUrl = (path = "/") => {
  if (!path || path === "/") return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export const PAGE_SEO = {
  "/": {
    title: "CA Jobs, CS Jobs & CMA Jobs in India | CAJobPortal",
    description:
      "India's first recruitment portal exclusive for CA, CS, and CMA. Apply to latest Chartered Accountant, Company Secretary, and CMA jobs from top companies.",
    h1: "Find Your Dream CA Job Today",
  },
  "/jobs": {
    title: "All CA, CS & CMA Jobs in India | CAJobPortal",
    description:
      "Browse all latest CA, CS, and CMA job openings in India. Filter by location, experience, and salary on CAJobPortal.",
    h1: "Jobs for CA, CS & CMA Professionals",
    intro:
      "Explore current openings for Chartered Accountants, Company Secretaries, and Cost Accountants across India.",
    source: "all",
  },
  "/ca-jobs": {
    title: "CA Jobs in India | Chartered Accountant Jobs | CAJobPortal",
    description:
      "Apply to the latest Chartered Accountant (CA) jobs in India. Find CA openings in audit, taxation, finance, and consulting on CAJobPortal.",
    h1: "CA Jobs",
    intro:
      "Latest Chartered Accountant jobs from firms, corporates, and consulting companies hiring CA professionals.",
    source: "all",
    qualificationKey: "ca",
  },
  "/cs-jobs": {
    title: "CS Jobs in India | Company Secretary Jobs | CAJobPortal",
    description:
      "Apply to Company Secretary (CS) jobs in India. Find CS openings in compliance, governance, and secretarial roles on CAJobPortal.",
    h1: "CS Jobs",
    intro:
      "Current Company Secretary jobs across listed companies, corporates, and professional firms.",
    source: "all",
    qualificationKey: "cs",
  },
  "/cma-jobs": {
    title: "CMA Jobs in India | Cost Accountant Jobs | CAJobPortal",
    description:
      "Apply to CMA and Cost Accountant jobs in India. Find cost accounting, FP&A, and finance openings on CAJobPortal.",
    h1: "CMA Jobs",
    intro:
      "Latest CMA and Cost & Management Accountant openings across industry and consulting.",
    source: "all",
    qualificationKey: "cma",
  },
  "/featured-job": {
    title: "Featured Jobs for CA, CS & CMA | CAJobPortal",
    description:
      "Browse featured CA, CS, and CMA jobs managed by CAJobPortal on behalf of hiring companies. Apply to premium finance openings.",
    h1: "Featured Jobs",
    intro: "Handpicked openings managed by CAJobPortal for CA, CS, and CMA professionals.",
    source: "featured",
  },
  "/career-sites": {
    title: "PSU Jobs & Career Site Openings | CAJobPortal",
    description:
      "Find PSU jobs and company career-site openings for CA, CS, and CMA professionals on CAJobPortal.",
    h1: "PSU Jobs / Career Sites",
    intro: "Government, PSU, and company career-site roles for finance professionals.",
    source: "career",
  },
  "/third-party-listing": {
    title: "Third Party Job Listings for CA, CS & CMA | CAJobPortal",
    description:
      "Browse third-party and search-firm job listings for CA, CS, and CMA professionals on CAJobPortal.",
    h1: "Third Party Listings",
    intro: "Roles posted by companies and search firms hiring CA, CS, and CMA talent.",
    source: "third-party",
  },
  "/recruiter": {
    title: "Hire CA, CS & CMA Professionals | For Employers | CAJobPortal",
    description:
      "Post jobs and hire qualified CA, CS, and CMA professionals. Access resumes and build your finance team with India's CA job portal.",
    h1: "Hire Your Next CA Professional",
  },
  "/post-job": {
    title: "Post a Job | Hire CA, CS & CMA | CAJobPortal",
    description:
      "Post a job on CAJobPortal and hire Chartered Accountants, Company Secretaries, and CMAs. Reach India's dedicated finance talent pool.",
    h1: "Post a Job and Hire Finance Talent",
  },
  "/about": {
    title: "About Us | India's Premier CA Job Portal | CAJobPortal",
    description:
      "CAJobPortal is India's first recruitment platform exclusively for CA, CS, and CMA professionals. Founded in 2013 by CAs and IIM Ahmedabad graduates.",
  },
  "/contact": {
    title: "Contact Us | CAJobPortal",
    description:
      "Contact CAJobPortal for hiring, job support, or partnership enquiries. India's recruitment portal for CA, CS, and CMA professionals.",
  },
  "/faq": {
    title: "Privacy Policy & FAQ | CAJobPortal",
    description:
      "Read the CAJobPortal privacy policy, terms, and FAQs. Learn how we protect candidate and employer data.",
  },
  "/milestone": {
    title: "Milestones & Media Coverage | CAJobPortal",
    description:
      "See CAJobPortal milestones and media coverage. India's first job portal exclusive for CA, CS, and CMA professionals.",
  },
};

export const SITELINK_PAGES = [
  { path: "/post-job", label: "Post a Job" },
  { path: "/ca-jobs", label: "CA Jobs" },
  { path: "/cs-jobs", label: "CS Jobs" },
  { path: "/cma-jobs", label: "CMA Jobs" },
  { path: "/featured-job", label: "Featured Jobs" },
  { path: "/career-sites", label: "PSU / Career Site Jobs" },
];

export const SOURCE_PATHS = {
  all: "/jobs",
  featured: "/featured-job",
  career: "/career-sites",
  "third-party": "/third-party-listing",
};

export const LISTING_PATHS = [
  "/jobs",
  "/ca-jobs",
  "/cs-jobs",
  "/cma-jobs",
  "/featured-job",
  "/career-sites",
  "/third-party-listing",
];

export const getListingPage = (pathname) =>
  LISTING_PATHS.includes(pathname) ? PAGE_SEO[pathname] : PAGE_SEO["/jobs"];

const QUALIFICATION_MATCHERS = {
  ca: (name) => /chartered accountant/i.test(name) || /(?:^|[^a-z])ca(?:[^a-z]|$)/i.test(name),
  cs: (name) => /company secretary/i.test(name) || /(?:^|[^a-z])cs(?:[^a-z]|$)/i.test(name),
  cma: (name) =>
    /cost accountant/i.test(name) ||
    /cost and management/i.test(name) ||
    /(?:^|[^a-z])cma(?:[^a-z]|$)/i.test(name),
};

export const matchQualificationNames = (qualifications = [], key) => {
  const test = QUALIFICATION_MATCHERS[key];
  if (!test) return [];
  return qualifications.map((item) => item?.name).filter(Boolean).filter(test);
};

export const QUALIFICATION_FALLBACK_KEYWORD = {
  ca: "CA",
  cs: "CS",
  cma: "CMA",
};
